import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  customerMe,
  customerLogout,
  customerListOrders,
  customerListGasStations,
  customerListProducts,
  type CustomerAuthResponse,
  type CustomerOrderResponse,
  type CustomerProductResponse,
  type ErrorResponse,
} from '@vn-propane/shared';
import { Button } from '@/components/ui/button';
import { LanguageSelector } from '@/components/ui/language-selector';
import {
  Bell,
  MapPin,
  Flame,
  Plus,
  ClipboardList,
  User,
  ChevronRight,
  CircleHelp,
} from 'lucide-react';

type OrderData = CustomerOrderResponse;

const ACTIVE_STATUSES = ['assigned', 'dispatched', 'in_progress', 'arrived'];

export default function HomePage() {
  const { t, i18n } = useTranslation(['home', 'orders', 'common', 'errors', 'auth']);
  const navigate = useNavigate();

  const [me, setMe] = useState<CustomerAuthResponse | null>(null);
  const [activeOrder, setActiveOrder] = useState<OrderData | null>(null);
  const [products, setProducts] = useState<CustomerProductResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [meRes, ordersRes, stationsRes] = await Promise.all([
          customerMe(),
          customerListOrders(),
          customerListGasStations(),
        ]);

        if (meRes.error) {
          const err = meRes.error as ErrorResponse;
          if (err.error?.code === '401') {
            navigate('/login');
            return;
          }
        }
        if (meRes.data?.data) {
          setMe(meRes.data.data);
        }

        if (ordersRes.data?.data) {
          const active = ordersRes.data.data.find((o) =>
            ACTIVE_STATUSES.includes(o.status.toLowerCase()),
          );
          if (active) setActiveOrder(active);
        }

        // Fetch products from the first gas station for the hero preview
        const firstStation = stationsRes.data?.data?.[0];
        if (firstStation) {
          const productsRes = await customerListProducts({
            path: { gas_station_id: firstStation.id },
          });
          if (productsRes.data?.data) {
            setProducts(productsRes.data.data.slice(0, 4));
          }
        }
      } catch {
        // Fail soft — home can render empty states
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, [navigate]);

  const formatCurrency = (amount: number | string) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      maximumFractionDigits: 0,
    }).format(num);
  };

  const greetingName = me?.username || me?.email?.split('@')[0] || t('common:appName');

  return (
    <div className="min-h-screen bg-muted/40 pb-20">
      {/* Hero */}
      <div className="relative bg-propane-500 text-white">
        <div
          className="absolute inset-0 opacity-90"
          style={{
            background: 'linear-gradient(135deg, #E8501C 0%, #C64012 100%)',
          }}
        />
        <div className="relative px-5 pt-6 pb-10">
          <div className="flex items-start justify-between mb-8">
            <div className="min-w-0">
              <div className="text-[13px] font-medium opacity-85">
                {t('home:hello')}, {greetingName}
              </div>
              <div className="flex items-center gap-1 text-[15px] font-bold mt-0.5 truncate">
                <MapPin className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{t('home:deliverTo')}</span>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <LanguageSelector />
              <a
                href={i18n.language === 'en' ? `${import.meta.env.VITE_HELP_URL || '/help'}/en/customer/` : `${import.meta.env.VITE_HELP_URL || '/help'}/customer/`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 grid place-items-center transition-colors"
                title={t('common:navigation.help')}
              >
                <CircleHelp className="h-[18px] w-[18px]" />
              </a>
              <button
                className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 grid place-items-center transition-colors"
                aria-label="Notifications"
              >
                <Bell className="h-[18px] w-[18px]" />
              </button>
            </div>
          </div>

          <div>
            <div className="text-3xl font-extrabold tracking-tight leading-[1.1]">
              {t('home:heroTitle')}
            </div>
            <div className="text-[13px] font-medium opacity-85 mt-1">
              {t('home:heroSubtitle')}
            </div>
            <Button
              onClick={() => navigate('/orders/new')}
              className="mt-4 bg-white text-propane-700 hover:bg-carbon-100 font-bold uppercase tracking-widest text-xs h-11"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              {t('newOrder')}
            </Button>
          </div>
        </div>
        <div className="h-1 bg-carbon-950" />
      </div>

      {/* Content */}
      <div className="px-4 pt-5">
        {/* Products */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-bold">
            {t('home:products')}
          </h2>
          <Link
            to="/orders/new"
            className="text-[11px] font-semibold uppercase tracking-widest text-propane-500 hover:text-propane-600"
          >
            {t('home:seeAll')}
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-2.5 mb-6">
          {products.length > 0
            ? products.slice(0, 4).map((p) => (
                <Link
                  key={p.id}
                  to="/orders/new"
                  className="bg-card border border-border rounded-sm p-3.5 flex flex-col gap-1.5 hover:shadow-sm transition-shadow"
                >
                  <div className="w-12 h-12 rounded-sm bg-propane-50 grid place-items-center text-propane-500">
                    <Flame className="h-6 w-6" />
                  </div>
                  <div className="text-sm font-bold truncate">{p.name}</div>
                  <div className="text-[12px] font-mono text-muted-foreground truncate">
                    {p.unit || ''}
                  </div>
                  <div className="text-base font-extrabold tabular-nums mt-0.5">
                    {formatCurrency(p.price)}
                  </div>
                </Link>
              ))
            : !isLoading && (
                <div className="col-span-2 text-center text-sm text-muted-foreground py-6 border border-dashed border-border rounded-sm">
                  {t('home:noProducts')}
                </div>
              )}
        </div>

        {/* Active order tracker */}
        {activeOrder && (
          <>
            <h2 className="text-base font-bold mb-3">
              {t('home:tracking')}
            </h2>
            <Link
              to={`/orders/${activeOrder.id}`}
              className="block bg-card border border-border rounded-sm p-3.5 hover:shadow-sm transition-shadow mb-6"
            >
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-sm font-bold font-mono">
                  GG-{activeOrder.id.slice(0, 6).toUpperCase()}
                </span>
                <span className="text-[10px] font-bold uppercase tracking-widest bg-warning text-carbon-950 px-2 py-1 rounded-sm inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-carbon-950" />
                  {t('status.in_progress')}
                </span>
              </div>
              <div
                className="h-24 rounded-sm relative overflow-hidden mb-2"
                style={{
                  background:
                    'repeating-linear-gradient(45deg, #E7E7E4 0 2px, #D1D1CD 2px 4px)',
                }}
              >
                <div
                  className="absolute w-7 h-7 border-[3px] border-white shadow-md"
                  style={{
                    left: '30%',
                    top: '70%',
                    background: '#1E6FBF',
                    borderRadius: '50% 50% 50% 0',
                    transform: 'translate(-50%, -100%) rotate(-45deg)',
                  }}
                />
                <div
                  className="absolute w-7 h-7 border-[3px] border-white shadow-md"
                  style={{
                    left: '70%',
                    top: '40%',
                    background: '#E8501C',
                    borderRadius: '50% 50% 50% 0',
                    transform: 'translate(-50%, -100%) rotate(-45deg)',
                  }}
                />
              </div>
              <div className="flex items-center justify-between text-[13px]">
                <span className="text-foreground font-medium">
                  {t('home:driverNear')}
                </span>
                <span className="text-propane-500 font-bold tabular-nums">
                  <ChevronRight className="h-4 w-4 inline -mb-0.5" />
                </span>
              </div>
            </Link>
          </>
        )}

        {/* Quick actions */}
        <div className="grid grid-cols-3 gap-2">
          <Link
            to="/orders"
            className="bg-card border border-border rounded-sm p-4 flex flex-col items-center gap-1.5 hover:bg-muted transition-colors"
          >
            <ClipboardList className="h-5 w-5 text-propane-500" />
            <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              {t('myOrders')}
            </span>
          </Link>
          <Link
            to="/orders/new"
            className="bg-card border border-border rounded-sm p-4 flex flex-col items-center gap-1.5 hover:bg-muted transition-colors"
          >
            <Plus className="h-5 w-5 text-propane-500" />
            <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              {t('newOrder')}
            </span>
          </Link>
          <button
            onClick={async () => {
              try {
                await customerLogout();
              } finally {
                navigate('/login');
              }
            }}
            className="bg-card border border-border rounded-sm p-4 flex flex-col items-center gap-1.5 hover:bg-muted transition-colors"
          >
            <User className="h-5 w-5 text-propane-500" />
            <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              {t('auth:logout')}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
